import { apiRequest } from "@core/services/api/apiRequest";
import { VIDEO_API } from "../videoMeeting.constants";
import type {
  VideoRoomHeartbeatResponse,
  DeclaredVideoState,
} from "../videoMeeting.types";
import type { CallQualityMetrics } from "./collectCallQualityMetrics";
import { getTrackingMetadata } from "./getTrackingMetadata";

export const sendVideoMeetingHeartbeat = async (
  meetingId: string,
  userName: string,
  user: { id: number } | null,
  qualityMetrics?: CallQualityMetrics | null,
  declaredState?: DeclaredVideoState | null,
  missedHeartbeats?: number,
  tabVisibility?: DocumentVisibilityState,
): Promise<VideoRoomHeartbeatResponse> => {
  const timestamp = Math.floor(Date.now() / 1000);
  const heartbeatKey = `${meetingId}_${userName}_${timestamp}`;
  const trackingMetadata = getTrackingMetadata(user);

  // connection_quality and missed_heartbeats appear both at the top level
  // (so the heartbeat handler can mirror them onto meeting_{mid}_{uid}
  // alongside the other declared-state fields) and nested inside
  // quality_metrics (where they feed the quality:{ts} sample archive).
  // The two readers want different things; don't dedupe.
  // See docs/video-architecture.md §17.
  //
  // tab_visibility is a top-level sibling field (NOT folded into
  // declaredState) — read from document.visibilityState at heartbeat
  // construction time in the hook. Mirrored to the user hash by the
  // heartbeat handler. See §10 for the audit-disambiguation rationale.
  return apiRequest(VIDEO_API.HEARTBEAT, "POST", {
    heartbeat_key: heartbeatKey,
    ...trackingMetadata,
    ...(declaredState ?? {}),
    ...(missedHeartbeats !== undefined ? { missed_heartbeats: missedHeartbeats } : {}),
    ...(tabVisibility !== undefined ? { tab_visibility: tabVisibility } : {}),
    ...(qualityMetrics ? { quality_metrics: qualityMetrics } : {}),
  });
};
